import React, { useRef, useState } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const dist = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

// Standard pinch-to-zoom + pan + double-tap, scoped to just this container — the app's
// global viewport disables native pinch-zoom everywhere else (for the native-app feel),
// so a document preview needs its own hand-rolled gesture handling to be zoomable at all.
// Deliberately unremarkable: no snapping, no rubber-banding, no velocity/inertia — just a
// direct 1:1 finger-follow like a plain image viewer, not a flashy interaction.
export default function ZoomablePreview({ children, className = "" }) {
  const [t, setT] = useState({ scale: 1, x: 0, y: 0 });
  const pinch = useRef(null);
  const pan = useRef(null);
  const lastTap = useRef(0);
  const gesturing = pinch.current || pan.current;

  const reset = () => setT({ scale: 1, x: 0, y: 0 });

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [t1, t2] = e.touches;
      pinch.current = { startDist: dist(t1, t2), startScale: t.scale };
      pan.current = null;
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        lastTap.current = 0;
        setT((cur) => (cur.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.2, x: 0, y: 0 }));
        return;
      }
      lastTap.current = now;
      if (t.scale > 1) {
        pan.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, origX: t.x, origY: t.y };
      }
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) {
      e.preventDefault();
      const [t1, t2] = e.touches;
      const ratio = dist(t1, t2) / pinch.current.startDist;
      const newScale = clamp(pinch.current.startScale * ratio, MIN_SCALE, MAX_SCALE);
      setT((cur) => ({ ...cur, scale: newScale }));
    } else if (e.touches.length === 1 && pan.current && t.scale > 1) {
      e.preventDefault();
      const dx = (e.touches[0].clientX - pan.current.startX) / t.scale;
      const dy = (e.touches[0].clientY - pan.current.startY) / t.scale;
      setT((cur) => ({ ...cur, x: pan.current.origX + dx, y: pan.current.origY + dy }));
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinch.current = null;
    if (e.touches.length === 0) {
      pan.current = null;
      // Snap back to 1x if pinched below it (finger slip) — otherwise leave scale as-is.
      if (t.scale < MIN_SCALE) reset();
    }
  };

  return (
    <div className={className} style={{ touchAction: "pan-y", overflow: "hidden" }}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      <div style={{
        transform: `scale(${t.scale}) translate(${t.x}px, ${t.y}px)`,
        transformOrigin: "top center",
        transition: gesturing ? "none" : "transform 0.15s ease-out",
      }}>
        {children}
      </div>
      {t.scale > 1 && (
        <button type="button" onClick={reset}
          className="fixed z-10 bottom-4 right-4 text-xs px-3 py-1.5 rounded-full bg-black/70 text-white border border-white/20 shadow-lg">
          Reset Zoom
        </button>
      )}
    </div>
  );
}
